import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabaseClient'

export const Login: React.FC = () => {
  const { loginMock, loginMockUser } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isMockMode, setIsMockMode] = useState(true)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    if (supabaseUrl && !supabaseUrl.includes('your-supabase-project')) {
      setIsMockMode(false)
    }

    if (!document.getElementById('google-gis-script')) {
      const script = document.createElement('script')
      script.id = 'google-gis-script'
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      document.body.appendChild(script)
    }
  }, [])

  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '129046717509-8t5m3v9710mehq63ammi0si95qkmtrhh.apps.googleusercontent.com'

  const handleGoogleSignIn = async () => {
    setError(null)
    if (isMockMode) {
      if (!(window as any).google?.accounts?.oauth2) {
        alert('⌛ ระบบยืนยันบัญชีของ Google กำลังโหลดกรุณารอสักครู่แล้วลองอีกครั้ง')
        return
      }

      setLoading(true)
      try {
        const client = (window as any).google.accounts.oauth2.initTokenClient({
          client_id: googleClientId,
          scope: 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/gmail.send',
          callback: (response: any) => {
            if (response.error) {
              setError(`เชื่อมต่อ Google ล้มเหลว: ${response.error_description || response.error}`)
              setLoading(false)
              return
            }
            if (response.access_token) {
              const exp = Date.now() + Number(response.expires_in) * 1000
              localStorage.setItem('google_drive_access_token', response.access_token)
              localStorage.setItem('google_drive_token_expiry', String(exp))
              window.dispatchEvent(new Event('google-token-updated'))

              // Mock login as teacher for demo
              loginMock('teacher')
            } else {
              setError('การยืนยันตัวตน Google ไม่ถูกต้อง')
              setLoading(false)
            }
          }
        })
        client.requestAccessToken({ prompt: '' })
      } catch (err: any) {
        setError(`เกิดข้อผิดพลาด: ${err.message}`)
        setLoading(false)
      }
    } else {
      setLoading(true)
      try {
        const { error: oauthError } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: window.location.origin + '/',
            scopes: 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/spreadsheets',
            queryParams: {
              access_type: 'offline',
              prompt: 'consent'
            }
          }
        })
        if (oauthError) throw oauthError
      } catch (err: any) {
        setError(err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ Google')
        setLoading(false)
      }
    }
  }

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    if (isMockMode) {
      try {
        const data = localStorage.getItem('tesscore_mock_data')
        const profiles = data ? JSON.parse(data).profiles : []
        const profile = profiles.find((p: any) => p.email === email || p.username === email)
        
        if (!profile) {
          throw new Error('ไม่พบบัญชีผู้ใช้งานในระบบ')
        }
        if (profile.password && profile.password !== password) {
          throw new Error('รหัสผ่านไม่ถูกต้อง')
        }
        if (profile.role === 'teacher' && profile.status === 'pending_approval') {
          throw new Error('🔒 บัญชีของคุณยังไม่ได้รับการอนุมัติจากผู้ดูแลระบบ กรุณารอการตรวจสอบและอนุมัติ')
        }
        
        loginMockUser(profile.id)
      } catch (err: any) {
        setError(err.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ')
        setLoading(false)
      }
      return
    }

    try {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (signInError) {
        throw new Error(signInError.message)
      }

      const loggedUserId = signInData.user?.id
      if (loggedUserId) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('status')
          .eq('id', loggedUserId)
          .single()

        if (profileError || !profile) {
          await supabase.auth.signOut()
          throw new Error('🔒 บัญชีผู้ใช้นี้ไม่มีอยู่ในระบบ หรือถูกลบออกจากฐานข้อมูลแล้ว')
        }

        if (profile.status === 'pending_approval') {
          await supabase.auth.signOut()
          throw new Error('🔒 บัญชีของคุณยังไม่ได้รับการอนุมัติจากผู้ดูแลระบบ กรุณารอการตรวจสอบและอนุมัติ')
        }
      }

      window.location.href = '/'
    } catch (err: any) {
      setError(err.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ')
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Orbs */}
      <div className="glow-orb glow-orb-primary" />
      <div className="glow-orb glow-orb-secondary" />

      <div className="glassmorphism" style={{ padding: '40px 32px', width: '100%', maxWidth: '440px', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: 850, 
            letterSpacing: '-1px', 
            marginBottom: '8px',
            background: 'linear-gradient(135deg, var(--text-primary) 30%, var(--primary) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            SciSynapseX
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: '1.4' }}>
            Integrated Science & Technology Education Platform
          </p>
        </div>

        {error && (
          <div style={{ 
            color: 'var(--danger)', 
            fontSize: '13px', 
            background: 'rgba(239, 68, 68, 0.1)', 
            padding: '12px', 
            borderRadius: '6px', 
            border: '1px solid rgba(239, 68, 68, 0.2)',
            marginBottom: '20px'
          }}>
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={handleLoginSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <label className="input-label" htmlFor="loginEmail">อีเมล หรือชื่อผู้ใช้</label>
            <input 
              id="loginEmail"
              type="text" 
              required 
              placeholder="เช่น teacher@school.ac.th หรือชื่อผู้ใช้"
              className="input-field" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label className="input-label" htmlFor="loginPassword" style={{ margin: 0 }}>รหัสผ่าน</label>
            </div>
            <div style={{ position: 'relative' }}>
              <input 
                id="loginPassword"
                type={showPassword ? 'text' : 'password'} 
                required 
                placeholder="••••••••"
                className="input-field" 
                style={{ width: '100%', paddingRight: '44px' }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-secondary)'
                }}
                title={showPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
              >
                {showPassword ? '👁️' : '🙈'}
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading}
            style={{ width: '100%', marginTop: '8px', height: '44px', fontSize: '15px' }}
          >
            {loading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '4px 0', color: 'var(--text-muted)', fontSize: '12px' }}>
            <div style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
            หรือ
            <div style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
          </div>

          <button 
            type="button" 
            onClick={handleGoogleSignIn}
            className="btn btn-outline" 
            style={{ 
              width: '100%', 
              height: '44px', 
              fontSize: '14px', 
              fontWeight: '700',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '10px',
              borderColor: 'var(--primary)',
              color: 'var(--primary)'
            }}
          >
            🔑 ล็อกอินด้วยบัญชี Google
          </button>
        </form>

        {isMockMode && (
          <div style={{
            marginTop: '28px',
            borderTop: '1px dashed var(--border)',
            paddingTop: '20px'
          }}>
            <span style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600', marginBottom: '12px', textAlign: 'center' }}>
              📢 โหมดสาธิต (Quick Demo Login)
            </span>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button 
                type="button" 
                onClick={() => loginMock('admin')}
                className="btn btn-outline"
                style={{ fontSize: '13px', padding: '8px 12px', width: '100%', justifyContent: 'flex-start', background: 'rgba(0, 184, 148, 0.05)', borderColor: 'rgba(0, 184, 148, 0.2)' }}
              >
                🔑 ล็อกอินด่วน: ผู้ดูแลระบบ (Admin)
              </button>
              <button 
                type="button" 
                onClick={() => loginMock('head_of_department')}
                className="btn btn-outline"
                style={{ fontSize: '13px', padding: '8px 12px', width: '100%', justifyContent: 'flex-start', background: 'rgba(9, 132, 227, 0.05)', borderColor: 'rgba(9, 132, 227, 0.2)' }}
              >
                🔑 ล็อกอินด่วน: หัวหน้ากลุ่มสาระ (HoD)
              </button>
              <button 
                type="button" 
                onClick={() => loginMock('teacher')}
                className="btn btn-outline"
                style={{ fontSize: '13px', padding: '8px 12px', width: '100%', justifyContent: 'flex-start' }}
              >
                🔑 ล็อกอินด่วน: ครูผู้สอนทั่วไป (Teacher)
              </button>
            </div>
          </div>
        )}

        <div style={{ marginTop: '28px', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', fontSize: '13px' }}>
          <Link to="/register" style={{ color: 'var(--primary)', fontWeight: '700', textDecoration: 'none' }}>
            📝 ลงทะเบียนสำหรับครูรายใหม่
          </Link>
          <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
            รุ่นระบบ 1.0 (React + Vite SPA)
          </div>
        </div>
      </div>
    </div>
  )
}
export default Login
